import React, { useCallback, useMemo, useState } from 'react';
import { navigate } from 'gatsby';
import { ApolloError, useMutation } from '@apollo/client';
import * as Sentry from '@sentry/gatsby';
import { TextInput } from '../ui/TextInput';
import { TextArea } from '../ui/TextArea';
import { LocationInput } from '../ui/LocationInput';
import { createCoworking } from '../../gql/mutations/createCoworking';
import { Button } from '../ui/Button';
import { useWebsiteContext } from '../../context';
import { WebsiteInput } from '../ui/WebsiteInput';

export const AddCoworkingModal = () => {
  const { setModalContent } = useWebsiteContext();
  const [locationName, setLocationName] = useState('');
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState<LocationData | null>(null);
  const [website, setWebsite] = useState('');
  const formDataValid = useMemo(
    () => locationName && location && website,
    [locationName, location, website],
  );

  const [submitCoworking, { loading: submitCoworkingLoading }] = useMutation(
    createCoworking,
    {
      onError: (error: ApolloError) => {
        console.error('Error creating location: ', error);
        Sentry.captureException(error, {
          tags: {
            component: 'CreateCoworking',
            query: 'createCoworking',
          },
        });
      },
      refetchQueries: ['getBusinessData'],
    },
  );

  const handleSubmit = useCallback(async () => {
    if (!formDataValid) return;

    try {
      const { data } = await submitCoworking({
        variables: {
          name: locationName,
          description,
          location,
          website,
        },
      });

      if (data?.createCoworking) {
        setModalContent(null);
        navigate(`/dashboard/${data.createCoworking.id}`);
      }
    } catch (error) {
      console.error('Error creating location: ', error);
      Sentry.captureException(error, {
        tags: {
          component: 'CreateCoworking',
          query: 'createCoworking',
        },
      });
    }
  }, [formDataValid, locationName, description, location, website]);

  return (
    <form
      onSubmit={e => {
        e.preventDefault();
        handleSubmit();
      }}
      className="flex flex-col gap-2"
    >
      <TextInput
        label="Name"
        value={locationName}
        onChange={setLocationName}
        placeholder='e.g. "The Coworking Hub"'
        required
      />

      <TextArea
        label="Description"
        value={description}
        onChange={setDescription}
        placeholder="Tell us about your coworking space. What makes it special? How many desks do you have? What are the hours and rates?"
      />

      <LocationInput onSelect={setLocation} required />

      <WebsiteInput
        label="Website"
        onValid={value => setWebsite(value || '')}
        required
      />

      <div className="flex items-center justify-end mt-2">
        <Button
          htmlType="submit"
          className="text-sm"
          disabled={!formDataValid}
          loading={submitCoworkingLoading}
        >
          Add Location
        </Button>
      </div>
    </form>
  );
};
